export function useUtils() {
	function debounce(fn, delay) {
		let timer;
		return function (...args) {
			clearTimeout(timer);
			timer = setTimeout(() => {
				fn(...args);
			}, delay);
		};
	}

	async function sha256(input) {
		const encoder = new TextEncoder();
		const data = encoder.encode(input);
		const hashBuffer = await crypto.subtle.digest('SHA-256', data);
		return Array.from(new Uint8Array(hashBuffer))
			.map(byte => byte.toString(16).padStart(2, '0'))
			.join('');
	}

	async function detectCountryByIP() {
		try {
			const response = await useApi('https://api.country.is/');
			if (response?.country) {
				return response.country.toLowerCase();
			}
		} catch (error) {
			console.error('IP country detection failed:', error);
		}
		return null;
	}

	const userInitialized = useState('userInitialized', () => false);
	let listenersAttached = false;
	let handleUserEngagement, removeListeners;
	let timeoutId = null;

	function initializeUserInteraction() {
		if (process.server) return;

		// If the user has already started interacting, there's no need for listeners
		if (userInitialized.value) return;

		// Check if user has already interacted (scroll position indicates interaction)
		if (window.scrollY > 50 || document.documentElement.scrollTop > 50) {
			userInitialized.value = true;
			return;
		}

		// Define functions if not already defined
		if (!handleUserEngagement) {
			handleUserEngagement = function () {
				userInitialized.value = true;
				removeListeners();
				if (timeoutId) {
					clearTimeout(timeoutId);
					timeoutId = null;
				}
			};

			removeListeners = function () {
				if (!listenersAttached) return;

				document.removeEventListener('pointermove', handleUserEngagement, true);
				document.removeEventListener('wheel', handleUserEngagement, true);
				document.removeEventListener('touchstart', handleUserEngagement, true);
				document.removeEventListener('touchmove', handleUserEngagement, true);
				document.removeEventListener('keydown', handleUserEngagement, true);
				document.removeEventListener('click', handleUserEngagement, true);
				document.removeEventListener('scroll', handleUserEngagement, true);

				listenersAttached = false;
			};
		}

		// Attach listeners to document with capture phase to catch events early
		if (!listenersAttached) {
			document.addEventListener('pointermove', handleUserEngagement, {passive: true, capture: true});
			document.addEventListener('wheel', handleUserEngagement, {passive: true, capture: true});
			document.addEventListener('touchstart', handleUserEngagement, {passive: true, capture: true});
			document.addEventListener('touchmove', handleUserEngagement, {passive: true, capture: true});
			document.addEventListener('keydown', handleUserEngagement, {capture: true});
			document.addEventListener('click', handleUserEngagement, {capture: true});
			document.addEventListener('scroll', handleUserEngagement, {passive: true, capture: true});

			listenersAttached = true;
		}

		// Auto-initialize after window load + 5 seconds if no user interaction
		if (!timeoutId) {
			timeoutId = setTimeout(() => {
				if (!userInitialized.value) {
					userInitialized.value = true;
					removeListeners();
				}
				timeoutId = null;
			}, 8000);
		}
	}

	return {debounce, sha256, detectCountryByIP, initializeUserInteraction, userInitialized};
}
